import { IBasePickerSuggestionsProps, IPersonaProps, ValidationState, NormalPeoplePicker, Label } from "@fluentui/react";
import React from "react";
import { useLazyGetUsersQuery } from "../../redux/api/graph/api-graph-slice";
import { IGraphGetUsersRequest, IGraphGetUsersResponse } from "../../redux/api/graph/api-graph-interfaces";
import authModule from "../../../../../../utilities/AuthModule";
import { logger } from "../../../../../../utilities/logger";
import { validateEmail } from "../../../helpers/validateEmail";

export interface ICustomPersonaProps extends IPersonaProps {
  isCustomEmail?: boolean;
  isGuest?: boolean;
}

export interface IPeopleFinderProps {
  label: string;
  disabled?: boolean;
  selectedPersonasChanged: (selectedPersonas: ICustomPersonaProps[], customTextValue?: string) => void;
}

const suggestionProps: IBasePickerSuggestionsProps = {
  noResultsFoundText: 'No results found',
  loadingText: 'Loading',
  showRemoveButtons: true,
  suggestionsAvailableAlertText: 'People Picker Suggestions available',
};

export const PeopleFinder = ({ label, disabled, selectedPersonasChanged }: IPeopleFinderProps) => {
  const [mostRecentlyUsed, setMostRecentlyUsed] = React.useState<IPersonaProps[]>([]);
  const selectedPersonas = React.useRef<IPersonaProps[]>([]);

  const [getUsers] = useLazyGetUsersQuery();

  const picker = React.useRef(null);

  const onFilterChanged = async (filterText: string) => {
    if (filterText) {
      const request: IGraphGetUsersRequest = {
        filter: filterText,
        orderby: 'displayName',
        select: 'id,displayName,mail,userPrincipalName,userType'
      }
      let result: any;

      try {
        result = await getUsers({ request, accessToken: await authModule.getGraphAccessToken() }).unwrap();
      } catch (error) {
        logger(error, "PeopleFinder.tsx (getUsers)");
      }

      if (result) {
        const users: IGraphGetUsersResponse[] = result.value;

        const personas: ICustomPersonaProps[] = [];
        if (users?.length > 0) {
          users.forEach(user => {
            const validatedEmailAddress: string = user.mail ?? (validateEmail(user.userPrincipalName) ? user.userPrincipalName : undefined);
            if (validatedEmailAddress) {
              personas.push({
                text: user.displayName,
                secondaryText: validatedEmailAddress,
                isGuest: user.userType === "Guest"
              });
            }
          });
        } else if (validateEmail(filterText)) {
          personas.push({
            text: filterText,
            secondaryText: filterText,
            isGuest: true,
            isCustomEmail: true
          });
        }

        const filteredPersonas = sortResults(filterText, removeDuplicates(personas, selectedPersonas.current));
        setMostRecentlyUsed(filteredPersonas);

        return filteredPersonas;
      }
    }

    return [];
  };

  const onSelectedPersonasChange = (items: IPersonaProps[]) => {
    //item added
    if (items?.length > selectedPersonas.current?.length) {
      selectedPersonas.current = items;
      setMostRecentlyUsed(removeDuplicates(mostRecentlyUsed, selectedPersonas.current));
    }
    //item removed
    else if (items?.length < selectedPersonas.current?.length) {
      const itemRemoved = selectedPersonas.current.find(x => !items.includes(x));

      const newMostRecentlyList: IPersonaProps[] = [...mostRecentlyUsed];
      newMostRecentlyList.push(itemRemoved);

      selectedPersonas.current = items;

      setMostRecentlyUsed(removeDuplicates(newMostRecentlyList.sort((a, b) => (a.secondaryText > b.secondaryText) ? 1 : ((b.secondaryText > a.secondaryText) ? -1 : 0)), selectedPersonas.current));
    }
  }

  return <div>
    <Label required>{label}</Label>
    <NormalPeoplePicker
      disabled={disabled}
      onChange={onSelectedPersonasChange}
      // eslint-disable-next-line react/jsx-no-bind
      onResolveSuggestions={onFilterChanged}
      // eslint-disable-next-line react/jsx-no-bind
      pickerSuggestionsProps={suggestionProps}
      className={'ms-PeoplePicker'}
      key={'controlled'}
      onValidateInput={validateInput}
      removeButtonAriaLabel={'Remove'}
      inputProps={{
        onBlur: (ev: React.FocusEvent<HTMLInputElement>) => {
          let customTextValue = "";
          if (!selectedPersonas.current.some(x => x.secondaryText?.toLowerCase() === ev.target.value?.toLowerCase() || x.primaryText?.toLowerCase() === ev.target.value?.toLowerCase())) {
            customTextValue = ev.target.value;
          }

          selectedPersonasChanged(selectedPersonas.current, customTextValue);
        }
      }}
      componentRef={picker}
      onInputChange={onInputChange}
      resolveDelay={300}
    />
  </div>;
};

const sortResults = (input: string, data: ICustomPersonaProps[]): ICustomPersonaProps[] => {
  try {
    let first: ICustomPersonaProps[] = [];
    let others: ICustomPersonaProps[] = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i].text.indexOf(input) == 0 || data[i].secondaryText.indexOf(input) == 0) {
        first.push(data[i]);
      } else {
        others.push(data[i]);
      }
    }
    first.sort((a, b) => a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1);
    others.sort((a, b) => a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1);
    // return (first.concat(others));
    return (first.concat(others).sort((a, b) => Number(a.isGuest) - Number(b.isGuest)));
  } catch (error) {
    console.warn(error);
  }
  return data;
}

function removeDuplicates(personas: IPersonaProps[], possibleDupes: IPersonaProps[]) {
  return personas.filter(persona => !listContainsPersona(persona, possibleDupes));
}

function listContainsPersona(persona: IPersonaProps, personas: IPersonaProps[]) {
  if (!personas || !personas.length || personas.length === 0) {
    return false;
  }
  return personas.filter(item => item.text === persona.text).length > 0;
}

function validateInput(input: string): ValidationState {
  if (input.indexOf('@') !== -1) {
    return ValidationState.valid;
  } else if (input.length > 1) {
    return ValidationState.warning;
  } else {
    return ValidationState.invalid;
  }
}

/**
 * Takes in the picker input and modifies it in whichever way
 * the caller wants, i.e. parsing entries copied from Outlook (sample
 * input: "Aaron Reid <aaron>").
 *
 * @param input The text entered into the picker.
 */
function onInputChange(input: string): string {
  const outlookRegEx = /<.*>/g;
  const emailAddress = outlookRegEx.exec(input);

  if (emailAddress && emailAddress[0]) {
    return emailAddress[0].substring(1, emailAddress[0].length - 1);
  }

  return input;
}